import IconCallingMelody from '@/context/global/components/Icon/IconCallingMelody';
import IconSidebarRightCollapse from '@/context/global/components/Icon/IconSidebarRightCollapse';
import { useKaraokeStore } from '@/context/global/stores/karaokeStore';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';

type MelodySidebarProps = {
  isOpen: boolean;
  onClose?: () => void;
};

const MelodySidebar = ({ isOpen, onClose }: MelodySidebarProps) => {
  const t = useTranslations('Karaoke.MelodySidebar');
  const { currentSong } = useKaraokeStore();

  const hasCallingMelody = !!currentSong?.callingMelodyUrl;
  const hasVdoMelody = !!currentSong?.vdoCallingMelodyUrl;
  const hasAnyMelody = hasCallingMelody || hasVdoMelody;

  const [activeTab, setActiveTab] = useState<'calling' | 'video'>(
    hasCallingMelody ? 'calling' : 'video',
  );

  useEffect(() => {
    if (hasCallingMelody) {
      setActiveTab('calling');
    } else if (hasVdoMelody) {
      setActiveTab('video');
    }
  }, [currentSong?.id, hasCallingMelody, hasVdoMelody]);

  const qrUrl =
    activeTab === 'calling'
      ? currentSong?.callingMelodyUrl
      : currentSong?.vdoCallingMelodyUrl;

  return (
    <div
      className={`flex h-full w-full flex-col transform border-l border-gray-800 bg-black ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      {/* Header */}
      <div className="border-b border-gray-800 p-4">
        <div className="flex items-center justify-between">
          <Image
            width={162}
            height={0}
            sizes="100vw"
            src="/assets/ais-karaoke-logo.png"
            alt="Karaoke Logo"
          />

          <button
            onClick={onClose}
            className="transform transition-all hover:scale-110"
            aria-label="Song List"
          >
            <IconSidebarRightCollapse />
          </button>
        </div>
      </div>

      {/* Tab Buttons */}
      {hasAnyMelody && (
        <div className="border-b border-gray-800 p-4">
          <div className="flex gap-3">
            {hasCallingMelody && (
              <button
                onClick={() => setActiveTab('calling')}
                className={`flex-1 rounded-lg border-[2.5px] border-white/10 px-2.5 py-2.5 text-[16px] font-medium transition-all ${
                  activeTab === 'calling'
                    ? 'bg-ais-green text-[#030606]'
                    : 'bg-transparent text-white hover:bg-white/5'
                }`}
              >
                {t('callingMelodyTab')}
              </button>
            )}
            {hasVdoMelody && (
              <button
                onClick={() => setActiveTab('video')}
                className={`flex-1 rounded-lg border-[2.5px] border-white/10 px-2.5 py-2.5 text-[16px] font-medium transition-all ${
                  activeTab === 'video'
                    ? 'bg-ais-green text-[#030606]'
                    : 'bg-transparent text-white hover:bg-white/5'
                }`}
              >
                {t('videoMelodyTab')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 overflow-y-auto p-5">
        {hasAnyMelody ? (
          <>
            {/* Title with Music Icon */}
            <div className="flex items-center justify-center gap-2.5">
              <IconCallingMelody />
              <h2 className="text-[24px] font-bold text-white">
                {activeTab === 'calling' ? t('callingMelodyTitle') : t('videoMelodyTitle')}
              </h2>
            </div>

            {/* QR Code */}
            <div className="flex h-[180px] w-[180px] items-center justify-center rounded-[18px] bg-white p-3 shadow-lg">
              {qrUrl && <QRCode value={qrUrl} size={156} />}
            </div>

            {/* Instructions */}
            <div className="text-center text-white">
              <p className="mb-1 text-[22px] font-medium">{t('scanQrTitle')}</p>
              <p className="text-[17px] leading-snug text-gray-400">
                {activeTab === 'calling' ? t('useAsCallingMelody') : t('useAsVideoMelody')}
              </p>
            </div>
          </>
        ) : (
          <div className="text-center text-white">
            <IconCallingMelody />
            <p className="mt-4 text-[22px] font-medium">{t('noMelodyAvailable')}</p>
            <p className="mt-2 text-[17px] text-gray-400">{t('forThisSong')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MelodySidebar;
